import {
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import { last } from "lodash";
import { lastValueFrom } from "rxjs";
import { AttachmentBackend } from "src/app/backend/api/attachment.backend";
import { StorageBackend } from "src/app/backend/api/storage/storage.backend";
import { PdfThumbnailService } from "src/app/services/pdf-thumbnail.service";
import { urlToDataUrl } from "src/app/support/blob";
import { isUrl } from "src/app/support/url";

const PDF_PLACEHOLDER = "/assets/placeholders/placeholder_document_pdf.png";
const INTERSECTION_ROOT_MARGIN = "200px";

@Directive({
  selector: "[pdfThumbnail]",
  standalone: false
})
export class PdfThumbnailDirective implements OnInit, OnChanges, OnDestroy {
  @Input("pdfThumbnail")
  storageId!: string;

  @Input("pdfThumbnailName")
  name: string | undefined;

  @Input("pdfIsAttachmentId")
  isAttachmentId: boolean | undefined;

  @Output()
  thumbnailGenerated: EventEmitter<string> = new EventEmitter<string>();

  readonly #attachmentBackend = inject(AttachmentBackend);
  readonly #storageBackend = inject(StorageBackend);
  readonly #pdfThumbnailService = inject(PdfThumbnailService);
  readonly #elementRef: ElementRef<HTMLElement> = inject(ElementRef);

  #intersectionObserver: IntersectionObserver | null = null;
  #objectUrl: string | null = null;
  #generationStarted = false;
  #fileExtension = "";

  ngOnInit(): void {
    this.#fileExtension = this.#getFileExtension();
    this.#showSpinner();

    switch (this.#fileExtension) {
      case "pdf": {
        this.#setupPdfLazyLoad();
        break;
      }
      case "doc":
      case "docx": {
        this.#showPlaceholder(
          "/assets/placeholders/placeholder_document_office.png"
        );
        break;
      }
      case "zip": {
        this.#showPlaceholder(
          "/assets/placeholders/placeholder_document_zip.png"
        );
        break;
      }
      case "jpg":
      case "jpeg":
      case "tiff":
      case "gif":
      case "png":
      case "bmp":
      case "webp": {
        void this.#handleImage();
        break;
      }
      case "mkv":
      case "flv":
      case "webm":
      case "mov":
      case "mp4":
      case "avi": {
        this.#showPlaceholder(
          "/assets/placeholders/placeholder_document_video.png"
        );
        break;
      }
      case "html": {
        this.#showPlaceholder(
          "/assets/placeholders/placeholder_document_html.png"
        );
        break;
      }
      default: {
        this.#showPlaceholder(
          "/assets/placeholders/placeholder_document_unknow.png"
        );
        break;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const storageIdChange = changes["storageId"];
    const attachmentIdChange = changes["isAttachmentId"];

    if (!storageIdChange && !attachmentIdChange) {
      return;
    }

    if (
      (storageIdChange?.firstChange ?? true) &&
      (attachmentIdChange?.firstChange ?? true)
    ) {
      return;
    }

    if (this.#fileExtension !== "pdf") {
      return;
    }

    this.#resetPdfThumbnailState();
    this.#setupPdfLazyLoad();
  }

  ngOnDestroy(): void {
    this.#intersectionObserver?.disconnect();
    this.#revokeObjectUrl();
  }

  #getFileExtension(): string {
    return (last(this.name?.split(".") || ["unknow"]) || "").toLowerCase();
  }

  #setupPdfLazyLoad(): void {
    this.#intersectionObserver?.disconnect();
    this.#generationStarted = false;

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (!entry?.isIntersecting || this.#generationStarted) {
          return;
        }

        this.#generationStarted = true;
        void this.#handlePdf();
      },
      { rootMargin: INTERSECTION_ROOT_MARGIN }
    );
    this.#intersectionObserver.observe(this.#elementRef.nativeElement);
  }

  #resetPdfThumbnailState(): void {
    this.#intersectionObserver?.disconnect();
    this.#generationStarted = false;
    this.#revokeObjectUrl();
    this.#showSpinner();
  }

  async #getObjectUrl(): Promise<string> {
    if (isUrl(this.storageId)) {
      return this.storageId;
    }

    if (this.isAttachmentId) {
      const attachment = await lastValueFrom(
        this.#attachmentBackend.load(this.storageId)
      );

      return lastValueFrom(this.#storageBackend.download(attachment.storageId));
    }

    return lastValueFrom(this.#storageBackend.download(this.storageId));
  }

  async #handlePdf(): Promise<void> {
    try {
      const blob = await this.#pdfThumbnailService.generateThumbnail({
        storageId: this.storageId,
        isAttachmentId: this.isAttachmentId
      });
      this.#revokeObjectUrl();
      this.#objectUrl = URL.createObjectURL(blob);
      this.#elementRef.nativeElement.innerHTML = `<img style="aspect-ratio: 1 / 1.4142" src="${this.#objectUrl}"/>`;
      this.thumbnailGenerated.emit(this.#objectUrl);
    } catch {
      this.#showPlaceholder(PDF_PLACEHOLDER);
    }
  }

  async #handleImage(): Promise<void> {
    if (!this.storageId) {
      this.#showPlaceholder(
        "/assets/placeholders/placeholder_document_image.png"
      );

      return;
    }

    const imageUrl = await this.#getObjectUrl();
    const dataUrl = await urlToDataUrl(imageUrl);
    this.#elementRef.nativeElement.innerHTML = `
        <div 
          class="flex-1" 
          style="background-image: url('${dataUrl}'); background-size: cover; background-position: center">
      </div>`;
  }

  #showSpinner(): void {
    this.#elementRef.nativeElement.innerHTML = `<div class="flex-1 flex items-center justify-center"><i class="fa-light fa-spinner-third animate-spin"></i></div>`;
  }

  #showPlaceholder(imgSrc: string): void {
    this.#elementRef.nativeElement.innerHTML = `
  <div class="flex-1 flex items-center justify-center">
    <img style="height: 56px;" src="${imgSrc}" style=""/>
  </div>`;
  }

  #revokeObjectUrl(): void {
    if (!this.#objectUrl) {
      return;
    }

    URL.revokeObjectURL(this.#objectUrl);
    this.#objectUrl = null;
  }
}
